import { useEffect, useRef, useCallback, useReducer } from 'react';

/**
 * A low-level hook to manage an HTMLAudioElement.
 * It handles the element's state, events, and provides basic controls.
 * This hook does not manage playlists, only the core audio playback.
 *
 * @returns An object with audio state and control functions.
 */
export const useAudioElement = () => {
  // --- State and Refs ---
  const audioRef = useRef(new Audio());
  const loadIdRef = useRef(0); // Guards against race conditions during source changes
  const lastTimeDispatchRef = useRef(0);

  const initialState = {
    isPlaying: false,
    duration: 0,
    currentTime: 0,
    volume: 1,
    isMuted: false,
    isLoading: false,
    error: null,
  };

  const audioReducer = (state, action) => {
    switch (action.type) {
      case 'SET_STATE':
        return { ...state, ...action.payload };
      case 'SET_PLAYING':
        return { ...state, isPlaying: action.payload, error: null };
      case 'SET_LOADING':
        return { ...state, isLoading: action.payload };
      case 'SET_TIME':
        return { ...state, currentTime: action.payload };
      case 'SET_DURATION':
        return { ...state, duration: action.payload };
      case 'SET_VOLUME':
        return { ...state, volume: action.payload.volume, isMuted: action.payload.isMuted };
      case 'SET_MUTED':
        return { ...state, isMuted: action.payload };
      case 'SET_ERROR':
        return { ...state, error: action.payload, isLoading: false, isPlaying: false };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(audioReducer, initialState);

  // Effect for wiring up audio element event listeners
  useEffect(() => {
    const audio = audioRef.current;
    audio.preload = 'auto';
    audio.crossOrigin = 'anonymous';

    const syncCurrentTime = () => dispatch({ type: 'SET_TIME', payload: audio.currentTime });
    const handleTimeUpdate = () => {
      const now = performance.now();
      if (now - lastTimeDispatchRef.current < 250) return;
      lastTimeDispatchRef.current = now;
      syncCurrentTime();
    };
    const handleLoadedMetadata = () => {
      dispatch({ type: 'SET_DURATION', payload: audio.duration });
      dispatch({ type: 'SET_LOADING', payload: false });
      dispatch({ type: 'SET_ERROR', payload: null });
    };
    const handlePlay = () => dispatch({ type: 'SET_PLAYING', payload: true });
    const handlePause = () => dispatch({ type: 'SET_PLAYING', payload: false });
    const handleWaiting = () => dispatch({ type: 'SET_LOADING', payload: true });
    const handleCanPlay = () => {
      dispatch({ type: 'SET_LOADING', payload: false });
      dispatch({ type: 'SET_ERROR', payload: null });
    };
    const handleError = () => {
      const err = audio.error;
      const detail = err ? `code=${err.code}` : 'unknown';
      dispatch({ type: 'SET_ERROR', payload: `Audio error (${detail}) for ${audio.currentSrc}` });
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('seeked', syncCurrentTime);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('seeked', syncCurrentTime);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('error', handleError);
    };
  }, []);

  const play = useCallback(() => {
    const attempt = audioRef.current.play();
    if (attempt?.catch) {
      return attempt.catch((e) => {
        const msg = e?.message || '';
        if (e?.name === 'AbortError' || /interrupted by a new load request/i.test(msg)) {
          // Swallow interruption errors so callers can retry once the new source is ready.
          return;
        }
        dispatch({ type: 'SET_ERROR', payload: `Playback failed: ${msg || 'Unknown error'}` });
        throw e;
      });
    }
    return Promise.resolve();
  }, []);

  const pause = useCallback(() => {
    audioRef.current.pause();
  }, []);

  const seek = useCallback((time) => {
    audioRef.current.currentTime = time;
  }, []);

  const setAudioVolume = useCallback((vol) => {
    const newVol = Math.max(0, Math.min(1, vol));
    audioRef.current.volume = newVol;
    dispatch({ type: 'SET_VOLUME', payload: { volume: newVol, isMuted: newVol === 0 } });
  }, []);

  const toggleMute = useCallback(() => {
    const muted = !audioRef.current.muted;
    audioRef.current.muted = muted;
    dispatch({ type: 'SET_MUTED', payload: muted });
  }, []);

  const loadAndPlay = useCallback((src) => {
    const audio = audioRef.current;
    const myLoadId = ++loadIdRef.current;

    audio.pause();
    dispatch({ type: 'SET_PLAYING', payload: false });

    if (audio.src !== src) {
      audio.src = src;
    }
    audio.currentTime = 0;
    dispatch({ type: 'SET_LOADING', payload: true });
    audio.load();

    const handleCanPlay = () => {
      if (loadIdRef.current !== myLoadId) return; // Stale request
      // Clear previous errors on successful load
      dispatch({ type: 'SET_ERROR', payload: null });
      play();
    };

    const handleLoadError = () => {
      const src = audio.currentSrc || audio.src;
      // Try alternate path once
      try {
        // swap "/api/audio/chronicles/" → "/api/chronicle/audio/"
        const alt = src.replace('/api/audio/chronicles/', '/api/chronicle/audio/');
        if (alt !== src) {
          console.warn(`[Audio] Retrying with alternate path: ${alt}`);
          audio.src = alt;
          audio.load();
          return; // let the canplay handler call play()
        }
      } catch {}
      // If no alternate path, let the main error handler deal with it.
    };

    audio.addEventListener('error', handleLoadError, { once: true });
    audio.addEventListener('canplay', handleCanPlay, { once: true });

    return () => {
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('error', handleLoadError);
    };
  }, [play]);

  return {
    audioRef,
    ...state,
    play,
    pause,
    seek,
    setVolume: setAudioVolume,
    toggleMute,
    loadAndPlay, // This is now superseded by the logic in useAudioPlayer
    dispatch, // Export the dispatch function
  };
};
